import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { debounce } from 'ts-debounce';
import { useShortLinkService } from '../services';

export const useShortLinkViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;

  const {
    loading: shortLinkLoading,
    filter: shortLinkFilter,
    list: shortLinkList,
    getShortLinks,
    changeShortLinkFilter,
    updateShortLink,
    deleteShortLink,
    toggleShortLinkStatus,
  } = useShortLinkService();

  const initLoad = useCallback(async () => {
    try {
      await getShortLinks(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, [currentCompanyId, getShortLinks]);

  const reloadShortLink = useCallback(async () => {
    try {
      await getShortLinks(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, [currentCompanyId, getShortLinks]);

  const reloadShortLinkDebounce = useMemo(() => debounce(reloadShortLink, 500), [reloadShortLink]);

  const changeShortLinkSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeShortLinkFilter({ query });
      await reloadShortLinkDebounce();
    },
    [changeShortLinkFilter, reloadShortLinkDebounce],
  );

  const changeShortLinkSorting = useCallback(
    async (s: GridSortModel) => {
      changeShortLinkFilter({ sort: `${s[0].field}_${s[0].sort}` });
      await reloadShortLink();
    },
    [changeShortLinkFilter, reloadShortLink],
  );

  const changeShortLinkPagination = useCallback(
    async (p: GridPaginationModel) => {
      changeShortLinkFilter({
        page: p.page,
        limit: p.pageSize,
        offset: p.page * p.pageSize,
      });
      await reloadShortLink();
    },
    [changeShortLinkFilter, reloadShortLink],
  );

  const removeShortLink = useCallback(
    async (linkId: string) => {
      try {
        await deleteShortLink(currentCompanyId, linkId);
        await reloadShortLink();
      } catch (e) {
        console.log(e);
      }
    },
    [currentCompanyId, deleteShortLink, reloadShortLink],
  );

  const toggleLinkStatus = useCallback(
    async (linkId: string, url: string, isActive: boolean, expiresAt?: string) => {
      try {
        await toggleShortLinkStatus(currentCompanyId, linkId, url, isActive, expiresAt ?? null);
        await reloadShortLink();
      } catch (e) {
        console.log(e);
      }
    },
    [currentCompanyId, toggleShortLinkStatus, reloadShortLink],
  );

  const updateLinkExpiration = useCallback(
    async (linkId: string, url: string, expiresAt?: string) => {
      try {
        await updateShortLink(currentCompanyId, linkId, {
          url,
          expiresAt: expiresAt ?? null,
          isActive: true,
        });
        await reloadShortLink();
      } catch (e) {
        console.log(e);
      }
    },
    [currentCompanyId, updateShortLink, reloadShortLink],
  );

  const updateShortLinkData = useCallback(
    async (linkId: string, updates: { url: string; expiresAt?: string }) => {
      try {
        const updateData: { url: string; expiresAt: string | null; isActive: boolean } = {
          url: updates.url,
          expiresAt: updates.expiresAt ?? null,
          isActive: true,
        };

        await updateShortLink(currentCompanyId, linkId, updateData);
        await reloadShortLink();
      } catch (e) {
        console.log(e);
      }
    },
    [currentCompanyId, updateShortLink, reloadShortLink],
  );

  const isPageLoading = shortLinkLoading.getList;

  useEffect(() => {
    initLoad();
  }, [initLoad]);

  return {
    isPageLoading,
    shortLinkFilter,
    shortLinkList,
    changeShortLinkSearchFilter,
    changeShortLinkSorting,
    changeShortLinkPagination,
    removeShortLink,
    toggleLinkStatus,
    updateLinkExpiration,
    updateShortLink: updateShortLinkData,
    reloadShortLink,
  };
};
